import React from 'react';
import Table from '../common/Table';
import ImagePreview from '../common/ImagePreview';
import { QuestionDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge, truncateText } from '../../utils/displayUtils';
import { CheckCircle2, XCircle } from 'lucide-react';

interface QuestionTableProps {
  data: QuestionDTO[];
  handleEdit: (row: QuestionDTO) => void;
  handleDelete: (row: QuestionDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const QuestionTable: React.FC<QuestionTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const renderOptions = (options: QuestionDTO['questionOptions']): JSX.Element => {
    if (!options || options.length === 0) {
      return (
        <div className="text-gray-500 text-sm">No options</div>
      );
    }

    return (
      <div className="space-y-1">
        {options.map((option) => (
          <div key={option.id} className="flex items-center text-xs">
            <span className="mr-2">
              {option.isCorrectOption ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
              )}
            </span>
            <span 
              className={option.isCorrectOption ? 'font-medium' : ''}
              title={option.title} // Show full text on hover
            >
              {truncateText(option.title ?? '', 40)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Table
      columns={[
        {
          label: 'Image',
          key: 'imageId',
          width: '80px',
          render: (row: QuestionDTO) => (
            <ImagePreview
              fileId={row.imageId}
              size="sm"
            />
          ),
        },
        {
          label: 'Question',
          key: 'title',
          render: (row: QuestionDTO) => (
            <div title={row.title}>
              {truncateText(row.title ?? '', 60)}
            </div>
          )
        },
        {
          label: 'Options',
          key: 'questionOptions',
          width: '300px',
          render: (row: QuestionDTO) => renderOptions(row.questionOptions),
        },
        {
          label: 'Is Active',
          key: 'isActive',
          width: '100px',
          render: (row: QuestionDTO) =>
            renderBooleanBadge(row.isActive ?? false),
        },
        {
          label: 'Added',
          key: 'createdUtc',
          width: '200px',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        onDelete: handleDelete,
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default QuestionTable;
